<template>
  <div class="min-h-screen" v-if="pageConfig">

    <PageHeader
      v-if="showPageHeader"
      :title="pageConfig.display_name"
      :subtitle="pageConfig.description"
    >
      <template v-if="tabList.length > 1" #bottom>
        <Page_Tabs
          :tabs="tabList"
          :initial-tab="activeTab"
          no-border
          @select="selectTab"
          class="px-6 pt-4"
        />
      </template>
    </PageHeader>

    <div v-else-if="tabList.length > 1" class="bg-gray-50 border-b border-gray-200">
      <Page_Tabs
        :tabs="tabList"
        :initial-tab="activeTab"
        no-border
        @select="selectTab"
        class="px-6 pt-4"
      />
    </div>

    <!-- Dynamic Sections -->
    <div class="p-6 space-y-4">
      <template v-for="(row, rowIndex) in sectionRows" :key="rowIndex">
        <div :class="row.length === 2 ? 'grid grid-cols-2 gap-6 items-stretch' : ''">
          <!-- selfChrome components provide their own card/border — render bare -->
          <template v-for="section in row" :key="section.id">
            <div v-if="sectionProvidesChrome(section)" class="flex flex-col">
              <component
                :is="getComponent(section.component)"
                v-bind="processSettings(section.settings)"
                :sectionId="section.id"
                :expanded="canExpand(section.component) ? !!sectionExpandedStates[section.id] : true"
                :fullWidth="!!sectionsAloneInRow[section.id]"
                class="h-full"
              />
            </div>
            <div v-else class="bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col">
              <ComponentHeader
                :title="section.title"
              >
                <button
                  v-if="canExpand(section.component)"
                  @click.stop="toggleSectionExpand(section.id)"
                  class="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                  :title="sectionExpandedStates[section.id] ? 'Collapse to compact view' : 'Expand to full view'"
                >
                  <svg v-if="!sectionExpandedStates[section.id]" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
                  </svg>
                  <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
                  </svg>
                </button>
              </ComponentHeader>
              <div class="p-4 flex-1">
                <component
                  :is="getComponent(section.component)"
                  v-bind="processSettings(section.settings)"
                  :sectionId="section.id"
                  :expanded="canExpand(section.component) ? !!sectionExpandedStates[section.id] : true"
                  :fullWidth="!!sectionsAloneInRow[section.id]"
                  class="h-full"
                  :class="section.component === 'UserInfoCard' ? '' : 'rounded-lg'"
                />
              </div>
            </div>
          </template>
        </div>
      </template>

    </div>
  </div>

  <!-- Loading State -->
  <div v-else class="min-h-screen flex">
    <LoadingIndicator message="Loading page configuration..." />
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import spiralDots from '@/assets/spiral_dots.svg'
import { COMPONENT_REGISTRY, getComponent, getComponentMeta, canComponentExpand } from '@/config/configurableComponentRegistry'
import { canAccessFeature } from '@/utils/access'
import PageHeader from './widgetcomponents/library/PageHeader.vue';
import ComponentHeader from './widgetcomponents/library/ComponentHeader.vue';
import LoadingIndicator from './widgetcomponents/library/LoadingIndicator.vue';
import Page_Tabs from './widgetcomponents/Page_Tabs.vue';

const DEFAULT_TAB = 'Overview'

export default {
  name: 'ConfigurablePage',
  components: {
    PageHeader,
    ComponentHeader,
    LoadingIndicator,
    Page_Tabs,
    // Dynamically register all components from registry
    ...Object.entries(COMPONENT_REGISTRY).reduce((acc, [key, value]) => {
      acc[key] = value.component
      return acc
    }, {})
  },
  props: {
    pageIdentifier: {
      type: String,
      required: true
    },
    context: {
      type: Object,
      default: () => ({})
    },
    showPageHeader: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      pageConfig: null,
      backgroundImage: spiralDots,
      showConfigModal: false,
      localLayoutConfig: null,  // For user customizations
      sectionExpandedStates: {},  // Track which sections are expanded
      activeTab: DEFAULT_TAB
    }
  },
  computed: {
    ...mapGetters('auth', ['current_user', 'isAuthenticated']),
    ...mapGetters('tenant', ['isTenantAdmin', 'current_tenant_roles']),

    canConfigure() {
      // Users can always customize their own view
      return this.isAuthenticated
    },

    effectiveLayoutConfig() {
      // Use local changes if they exist, otherwise use page config
      return this.localLayoutConfig || this.pageConfig?.layout_config || {}
    },

    allVisibleSections() {
      if (!this.effectiveLayoutConfig.sections) return []
      return this.effectiveLayoutConfig.sections
        .filter(s => s.visible !== false && this.hasRoleAccess(s))
        .sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
    },

    tabList() {
      const seen = new Set()
      const tabs = []
      for (const section of this.allVisibleSections) {
        const tab = section.tab || DEFAULT_TAB
        if (!seen.has(tab)) {
          seen.add(tab)
          tabs.push(tab)
        }
      }
      if (!seen.has(DEFAULT_TAB)) {
        tabs.unshift(DEFAULT_TAB)
      }
      return tabs
    },

    visibleSections() {
      return this.allVisibleSections.filter(
        s => (s.tab || DEFAULT_TAB) === this.activeTab
      )
    },

    hiddenSections() {
      if (!this.effectiveLayoutConfig.sections) return []
      return this.effectiveLayoutConfig.sections
        .filter(s => s.visible === false)
        .sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
    },

    sectionRows() {
      // Group consecutive half-width sections into pairs
      // Full-width sections get their own row
      // Expanded half-width sections are treated as full-width
      const rows = []
      let currentHalfRow = []

      for (const section of this.visibleSections) {
        const isExpanded = this.sectionExpandedStates[section.id]
        const effectiveWidth = (section.width === 'half' && !isExpanded) ? 'half' : 'full'

        if (effectiveWidth === 'half') {
          currentHalfRow.push(section)
          if (currentHalfRow.length === 2) {
            rows.push(currentHalfRow)
            currentHalfRow = []
          }
        } else {
          // Full width, unspecified, or expanded half-width
          if (currentHalfRow.length > 0) {
            rows.push(currentHalfRow)
            currentHalfRow = []
          }
          rows.push([section])
        }
      }
      // Don't forget trailing half-width section
      if (currentHalfRow.length > 0) {
        rows.push(currentHalfRow)
      }
      return rows
    },

    // Track which sections are alone in their row (for responsive rendering)
    sectionsAloneInRow() {
      const alone = {}
      for (const row of this.sectionRows) {
        if (row.length === 1 && row[0].width === 'half') {
          alone[row[0].id] = true
        }
      }
      return alone
    },

    tabStorageKey() {
      return `configurable_page_tab_${this.pageIdentifier}`
    }
  },
  async mounted() {
    await this.fetchPageConfig()
  },
  methods: {
    async fetchPageConfig() {
      try {
        const response = await axios.get(
          `/api/page-configurations/by-identifier/${this.pageIdentifier}/`
        )
        this.pageConfig = response.data
        // Initialize local config from server config
        this.localLayoutConfig = JSON.parse(JSON.stringify(this.pageConfig.layout_config))
        this.restoreActiveTab()
        // Load expanded states after config is loaded
        this.$nextTick(() => this.loadExpandedStates())
      } catch (error) {
        console.error('Error fetching page config:', error)
        this.$router.push({ name: 'NotFound' })
      }
    },

    getComponent(componentName) {
      // Use the registry to get component
      return getComponent(componentName) ? componentName : null
    },

    processSettings(settings) {
      // Replace dynamic placeholders like CURRENT_USER_ID
      const processed = JSON.parse(JSON.stringify(settings))

      const replacePlaceholders = (obj) => {
        for (let key in obj) {
          if (typeof obj[key] === 'string' && obj[key] === 'CURRENT_USER_ID') {
            obj[key] = this.current_user.id
          } else if (typeof obj[key] === 'object' && obj[key] !== null) {
            replacePlaceholders(obj[key])
          }
        }
      }

      replacePlaceholders(processed)
      return processed
    },

    selectTab(tab) {
      this.activeTab = tab
      localStorage.setItem(this.tabStorageKey, tab)
    },

    restoreActiveTab() {
      const stored = localStorage.getItem(this.tabStorageKey)
      if (stored && this.tabList.includes(stored)) {
        this.activeTab = stored
      } else {
        this.activeTab = this.tabList[0] || DEFAULT_TAB
      }
    },

    async toggleSectionVisibility(sectionId) {
      // Find and toggle the section
      const section = this.localLayoutConfig.sections.find(s => s.id === sectionId)
      if (section) {
        section.visible = !section.visible
        // Save to backend
        await this.saveUserConfig()
      }
    },

    async saveUserConfig() {
      try {
        await axios.post('/api/page-configurations/save-user-config/', {
          page_identifier: this.pageIdentifier,
          layout_config: this.localLayoutConfig
        })
        this.$emit('config-saved')
      } catch (error) {
        console.error('Error saving user config:', error)
        alert('Failed to save configuration. Please try again.')
      }
    },

    canExpand(componentName) {
      return canComponentExpand(componentName)
    },

    sectionProvidesChrome(section) {
      if (section.settings?.self_chrome != null) return section.settings.self_chrome
      const defaults = getComponentMeta(section.component)?.defaultSettings
      return defaults?.self_chrome === true
    },

    hasRoleAccess(section) {
      if (section.required_feature && !canAccessFeature(section.required_feature)) {
        return false
      }
      // If no visible_roles specified, section is visible to all
      if (!section.visible_roles || !Array.isArray(section.visible_roles) || section.visible_roles.length === 0) {
        return true
      }
      // User must have at least one of the specified roles
      const userRoles = this.current_tenant_roles || []
      return section.visible_roles.some(role => userRoles.includes(role))
    },

    toggleSectionExpand(sectionId) {
      const currentState = !!this.sectionExpandedStates[sectionId]
      this.sectionExpandedStates[sectionId] = !currentState
      // Persist to localStorage
      const section = this.visibleSections.find(s => s.id === sectionId)
      if (section?.settings?.persist_key) {
        localStorage.setItem(section.settings.persist_key + '_expanded', JSON.stringify(!currentState))
      }
    },

    loadExpandedStates() {
      // Load expanded states from localStorage for all expandable sections
      for (const section of this.allVisibleSections) {
        if (this.canExpand(section.component) && section.settings?.persist_key) {
          const stored = localStorage.getItem(section.settings.persist_key + '_expanded')
          if (stored !== null) {
            this.sectionExpandedStates[section.id] = JSON.parse(stored)
          }
        }
      }
    }
  }
}
</script>

<style scoped>
/* Component-specific styles */
</style>
